import CustomTooltip from '../../shared/CustomTooltip';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Grid, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    useAddIndividualFeeMutation,
    useDeleteFeeMutation,
    useEditFeeMutation,
    useGetFeeListQuery,
} from '../../../app/Slices/SevenDayApiSlice';
import { CustomButton } from '../../../layout/styledcomponents/CustomButton';
import UserRole from '../../../shared/AuthRoles';
import { camelCaseToTitleCase, capitalizeName } from '../../../shared/utils';
import ConfirmationModal from '../../shared/ConfirmationModal';
import toast from '../../shared/Toast';
import { Fee } from './FeesList';
import { z } from 'zod';
import { ExperienceType, FeeType } from '../../enums/experience';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import FormTextField from '../../shared/FormTextField';
import FormMultiAutocomplete from '../../shared/FormMultiAutocomplete';
import FormSelect from '../../shared/FormSelect';

interface Props {
    fee?: Fee;
}

const FeeForm = ({ fee }: Props) => {
    const [addNewFee] = useAddIndividualFeeMutation();
    const [feeName, setFeeName] = useState('');
    const loggedInUser = UserRole();
    const navigate = useNavigate();
    const [editFee] = useEditFeeMutation();
    const [deleteFee] = useDeleteFeeMutation();
    const { data: getFees } = useGetFeeListQuery(loggedInUser?.company);
    const [showModal, setShowModal] = useState(false);

    const validateName = (name: string) => {
        const findDuplicate = feeNames?.find(
            (feeName: string) => feeName === name,
        );

        if (findDuplicate !== undefined && findDuplicate !== getFees?.fee?.name)
            return false;

        return true;
    };

    const Schema = z.object({
        name: z
            .string()
            .min(1, { message: 'Required' })
            .refine(validateName, { message: 'Fee already exists' }),
        type: z.nativeEnum(FeeType, { message: 'Required' }),
        moduleTypes: z
            .array(z.nativeEnum(ExperienceType))
            .nonempty({ message: 'At least 1 experience type required' }),
    });

    type FormValues = z.infer<typeof Schema>;

    const methods = useForm<FormValues>({
        defaultValues: {
            name: '',
            moduleTypes: [],
            type: '' as FeeType,
        },
        resolver: zodResolver(Schema),
    });

    const { control, handleSubmit, reset } = methods;

    const feeNames = getFees
        ?.filter((f: Fee) => f.id !== fee?.id)
        ?.map((fee: Fee) => fee?.name);

    const onSubmit = async (data: FormValues) => {
        try {
            if (fee) {
                const response: Fee = await editFee([
                    loggedInUser?.company,
                    fee.id,
                    data,
                ]).unwrap();

                toast.success(`Fee: ${response.name} has been updated`);
            } else {
                const response: Fee = await addNewFee([
                    loggedInUser?.company,
                    data,
                ]).unwrap();

                toast.success(
                    `New fee type: ${response.name} has been created`,
                );
            }

            navigate('/fees');
        } catch (error: any) {
            toast.error(error?.data.message);
        }
    };

    const handleDeleteFee = () => {
        deleteFee([loggedInUser?.company, fee?.id]).then((data: any) => {
            if (data.error) {
                toast.error(`${data.error?.data?.message}`);
            } else {
                toast.success(`Fee: ${feeName} has been deleted`);
                navigate('/fees');
            }
        });
    };

    useEffect(() => {
        // @ts-ignore
        if (fee) reset(fee);
    }, [fee]);

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Grid
                    sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '40px',
                    }}
                >
                    <Grid
                        sx={{
                            width: '100%',
                            justifyContent: 'left',
                            display: 'flex',
                        }}
                    >
                        <Typography
                            variant='h1'
                            sx={{
                                textAlign: 'left',
                                textTransform: 'uppercase',
                            }}
                        >
                            {fee
                                ? `Edit Pricing / Fee `
                                : ` New Pricing / Fee Type`}
                        </Typography>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid item container columns={25} spacing={1}>
                            <Grid item xs={25} lg={4}>
                                <Typography sx={{ color: 'text.primary' }}>
                                    Fee Name
                                    {feeName === '' ? (
                                        <span
                                            style={{
                                                color: 'red',
                                            }}
                                        >
                                            *
                                        </span>
                                    ) : null}{' '}
                                </Typography>
                            </Grid>
                            <Grid item xs={25} lg={9}>
                                <FormTextField name='name' control={control} />
                            </Grid>
                        </Grid>
                        <Grid container item columns={25} spacing={1}>
                            <Grid
                                item
                                xs={25}
                                lg={4}
                                sx={{
                                    display: 'flex',
                                    alignContent: 'center',
                                    gap: '3px',
                                }}
                            >
                                {' '}
                                <Typography>Experience Type</Typography>
                                <Grid>
                                    <CustomTooltip
                                        title={
                                            <Typography>
                                                Selecting this does not apply
                                                this to all experiences with
                                                this experience type. To do so,
                                                you will need to go to the
                                                experience and apply the Pricing
                                                & Fee amount. By selecting the
                                                Experience Types here, it will
                                                appear in that experience types
                                                'Pricing & Fees List’ as an
                                                option to add.
                                            </Typography>
                                        }
                                        placement='right'
                                    >
                                        <HelpOutlineIcon
                                            sx={{
                                                fontSize: '16px',
                                            }}
                                        />
                                    </CustomTooltip>
                                </Grid>
                            </Grid>
                            <Grid item xs={25} lg={9}>
                                <FormMultiAutocomplete
                                    name='moduleTypes'
                                    control={control}
                                    options={Object.values(ExperienceType).map(
                                        (type) => ({
                                            value: type,
                                            label: capitalizeName(type),
                                        }),
                                    )}
                                />
                            </Grid>
                        </Grid>
                        <Grid container item columns={25} spacing={1}>
                            <Grid
                                item
                                xs={25}
                                lg={4}
                                sx={{
                                    display: 'flex',
                                    alignContent: 'center',
                                    gap: '3px',
                                }}
                            >
                                {' '}
                                <Typography>Fee Type</Typography>
                            </Grid>
                            <Grid item xs={25} lg={9}>
                                <FormSelect
                                    name='type'
                                    control={control}
                                    options={Object.values(FeeType).map(
                                        (value) => {
                                            return {
                                                value: value,
                                                label: camelCaseToTitleCase(
                                                    value,
                                                ),
                                            };
                                        },
                                    )}
                                />
                            </Grid>
                        </Grid>

                        <Grid
                            container
                            item
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <Grid
                                sx={{
                                    display: 'flex',
                                    gap: '10px',
                                }}
                            >
                                {fee && (
                                    <CustomButton
                                        id='deleteFeeType'
                                        variant='contained'
                                        color='warning'
                                        onClick={() => {
                                            setShowModal(!showModal);
                                        }}
                                    >
                                        Delete
                                    </CustomButton>
                                )}

                                <CustomButton
                                    type='submit'
                                    variant='contained'
                                    color='primary'
                                >
                                    {fee ? `Update` : ` Save`}
                                </CustomButton>
                            </Grid>

                            <ConfirmationModal
                                open={showModal}
                                onClose={() => setShowModal(false)}
                                onChange={handleDeleteFee}
                                message={'delete this Pricing & Fee type'}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </form>
        </FormProvider>
    );
};

export default FeeForm;
